import { useState, useEffect, useRef } from "react";
import "./AddFaqs.scss";
import { useCollection } from "../firebase-files/hooks/useCollection";
import { useFirestore } from "../firebase-files/hooks/useFirestore";

const SUCCESS_MESSAGE_TIMEOUT_MS = 3000;

const AddFaqs = () => {
  const { addDocument } = useFirestore("faqs");
  const { documents: faqDoc } = useCollection("m_categories", [
    "source",
    "==",
    "faq",
  ]);

  const [showPopup, setShowPopup] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [category, setCategory] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const popupRef = useRef(null);
  const questionRef = useRef(null);

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowPopup(false);
      }
    };
    if (showPopup) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showPopup]);

  // Focus management when popup opens
  useEffect(() => {
    if (showPopup && questionRef.current) {
      questionRef.current.focus();
    }
  }, [showPopup]);

  const handleAddFaq = async () => {
    if (!question.trim() || !answer.trim() || !category.trim()) {
      setErrorMessage("Please fill in all fields.");
      return;
    }

    setErrorMessage("");
    setLoading(true);

    const newFaqs = {
      question,
      answer,
      category,
      status: "active",
    };

    try {
      await addDocument(newFaqs);
      setQuestion("");
      setAnswer("");
      setCategory("");
      setSuccessMessage(true);
      setTimeout(() => {
        setSuccessMessage(false);
        setShowPopup(false);
      }, SUCCESS_MESSAGE_TIMEOUT_MS);
    } catch (error) {
      console.error("Error adding FAQ:", error);
      setErrorMessage("Failed to add FAQ. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-faq-container">
      <button
        className="add-faq-top-btn"
        onClick={() => {
          setShowPopup(true);
          setSuccessMessage(false);
          setErrorMessage("");
        }}
      >
        + Add FAQ
      </button>

      {showPopup && (
        <div
          className="popup-overlay"
          onClick={() => setShowPopup(false)}
        >
          <div
            className="popup-box"
            onClick={(e) => e.stopPropagation()}
            ref={popupRef}
          >
            <h3>Add a New FAQ</h3>

            <div className="form-group">
              <label>Question</label>
              <input
                type="text"
                placeholder="Type your question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                ref={questionRef}
              />
            </div>

            <div className="form-group">
              <label>Answer</label>
              <textarea
                placeholder="Type the answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              ></textarea>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="" disabled>
                  Select Category
                </option>
                {faqDoc &&
                  faqDoc.map((cat) => (
                    <option key={cat.id} value={cat.category}>
                      {cat.category}
                    </option>
                  ))}
              </select>
            </div>

            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}

            {successMessage && (
              <div className="popup-success-msg">
                ✅ FAQ added successfully!
              </div>
            )}

            <div className="popup-buttons">
              <button
                className="cancel-btn"
                onClick={() => setShowPopup(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="add-btn"
                onClick={handleAddFaq}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddFaqs;
